import { lazy } from "react";

// Post-deploy chunk recovery.
//
// The app is code-split: each route loads as a hashed chunk
// (e.g. `87.990154d2.chunk.js`). When a NEW build is deployed the
// chunk hashes change and the OLD chunk files are removed from the
// server. A browser still running the PREVIOUS index.html/main.js will
// request a now-deleted chunk URL → 404 → `ChunkLoadError`.
//
// Recovery: on a chunk-load failure, force ONE full page reload so the
// browser fetches the fresh index.html + chunk manifest. A
// sessionStorage flag prevents an infinite reload loop if the failure
// is for some other (genuine) reason.

const RELOAD_FLAG = "chunk_reload_attempted";

export function isChunkLoadError(err) {
  if (!err) return false;
  const name = err.name || "";
  const msg = err.message || String(err) || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    /failed to (?:fetch dynamically imported|import) module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

// Returns true if a reload was triggered (caller should stop further
// handling). No-ops + returns false when we've already reloaded once
// this session, so genuine errors still surface.
export function reloadOnceForChunkError(err) {
  try {
    if (!isChunkLoadError(err)) return false;
    if (window.sessionStorage.getItem(RELOAD_FLAG) === "1") return false;
    window.sessionStorage.setItem(RELOAD_FLAG, "1");
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

export function lazyWithRetry(importer) {
  return lazy(async () => {
    try {
      const mod = await importer();
      // Successful load — clear the guard so a future deploy can
      // recover again.
      try {
        window.sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        /* ignore */
      }
      return mod;
    } catch (err) {
      if (reloadOnceForChunkError(err)) {
        // Never-resolving promise: keep React in Suspense fallback
        // until the reload navigates away, instead of flashing the
        // error boundary.
        return new Promise(() => {});
      }
      throw err;
    }
  });
}
